/**
 * @description
 * Marketplace configuration page for system-level marketplace settings.
 * Manages Cloudflare credentials, deployment settings, and security configurations.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-10
 */

import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { MarketplaceConfigNavigation } from './MarketplaceConfigNavigation';
import { SystemConfiguration } from './SystemConfiguration';
import { DeploymentSettings } from './DeploymentSettings';
import { SecuritySettings } from './SecuritySettings';
import { AnalyticsSettings } from './AnalyticsSettings';
import { GitHubSettings } from './GitHubSettings';
import { CloudflareSettings } from './CloudflareSettings';
import { TestDeployment } from './TestDeployment';
import { useMarketplaceConfig } from './hooks/useMarketplaceConfig';
import { MarketplaceConfigSection } from './types';

/**
 * Marketplace configuration page component
 */
export function MarketplaceConfig() {
  // User authentication is handled by the parent component
  useAuth();
  const [activeSection, setActiveSection] = useState<MarketplaceConfigSection>('system');
  const { config, loading, refetch } = useMarketplaceConfig();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  const renderConfigContent = () => {
    switch (activeSection) {
      case 'system':
        return <SystemConfiguration onRefresh={refetch} />;
      case 'deployment':
        return <DeploymentSettings onRefresh={refetch} />;
      case 'security':
        return <SecuritySettings onRefresh={refetch} />;
      case 'analytics':
        return <AnalyticsSettings onRefresh={refetch} />;
      case 'github':
        return <GitHubSettings onRefresh={refetch} />;
      case 'cloudflare':
        return <CloudflareSettings onRefresh={refetch} />;
      case 'test':
        return <TestDeployment onRefresh={refetch} />;
      default:
        return <SystemConfiguration onRefresh={refetch} />;
    }
  };

  return (
    <div className="space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Marketplace Configuration</h1>
        <p className="text-muted-foreground">
          Manage system configuration for marketplace functionality including Cloudflare credentials and deployment settings
        </p>
      </div>

      {/* Configuration Status */}
      {config && (
        <div className="rounded-lg border bg-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Configuration Status</h3>
              <p className="text-sm text-muted-foreground">
                {config.id ? 'Configured' : 'Not configured'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Last updated</p>
              <p className="text-sm font-medium">{config.updatedAt}</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Navigation Sidebar */}
        <div className="lg:col-span-1">
          <MarketplaceConfigNavigation
            activeSection={activeSection}
            onSectionChange={setActiveSection}
          />
        </div>

        {/* Configuration Content */}
        <div className="lg:col-span-3">
          <div className="rounded-lg border bg-card p-6">
            {renderConfigContent()}
          </div>
        </div>
      </div>
    </div>
  );
}